import { useMemo, useState } from 'react';

import FilterBar from './components/FilterBar.jsx';
import HackathonCard from './components/HackathonCard.jsx';
import LocationBar from './components/LocationBar.jsx';
import SourceStrip from './components/SourceStrip.jsx';
import { dedupeHackathons } from './lib/dedupe.js';
import { withDistances } from './lib/geo.js';
import { DEFAULT_FILTERS, applyFilters, collectThemes } from './lib/filters.js';
import { useHackathons } from './lib/useHackathons.js';
import { useUserLocation } from './lib/useUserLocation.js';

export default function App() {
  const { sourceStates, records, loading, reload } = useHackathons();
  const {
    location,
    status: locationStatus,
    error: locationError,
    requestBrowser,
    setManualLocation,
    clearLocation,
  } = useUserLocation();

  const [filters, setFilters] = useState(DEFAULT_FILTERS);

  // Merge -> dedupe -> distance. Recomputed only when sources or location change.
  const merged = useMemo(() => dedupeHackathons(records), [records]);
  const withDistance = useMemo(() => withDistances(merged, location), [merged, location]);

  const themes = useMemo(() => collectThemes(withDistance), [withDistance]);

  const { visible, expiredCount, unlocatedHidden } = useMemo(
    () => applyFilters(withDistance, filters, location),
    [withDistance, filters, location],
  );

  const duplicatesMerged = records.length - merged.length;
  const anyLoaded = sourceStates.some((s) => s.status === 'ok');

  return (
    <div className="app">
      <header className="masthead">
        <div className="masthead-inner">
          <div className="brand">
            <h1>HackFind</h1>
            <p className="tagline">Hackathons from Devpost, MLH and Hack Club, in one place.</p>
          </div>
          <LocationBar
            location={location}
            status={locationStatus}
            error={locationError}
            onRequestBrowser={requestBrowser}
            onManualLocation={setManualLocation}
            onClear={clearLocation}
          />
        </div>
      </header>

      <main className="main">
        <SourceStrip sourceStates={sourceStates} onReload={reload} loading={loading} />

        <FilterBar
          filters={filters}
          onChange={setFilters}
          availableThemes={themes}
          hasLocation={Boolean(location)}
          onReset={() => setFilters(DEFAULT_FILTERS)}
        />

        <div className="results-summary" role="status">
          <strong>{visible.length}</strong> hackathon{visible.length === 1 ? '' : 's'}
          {duplicatesMerged > 0 ? (
            <span className="summary-note"> · {duplicatesMerged} cross-listed merged</span>
          ) : null}
          {expiredCount > 0 ? (
            <span className="summary-note"> · {expiredCount} closed or finished hidden</span>
          ) : null}
          {unlocatedHidden > 0 ? (
            <span className="summary-note">
              {' '}
              · {unlocatedHidden} outside radius check (venue not geocodable)
            </span>
          ) : null}
        </div>

        {loading && !anyLoaded ? (
          <div className="grid" aria-hidden="true">
            {Array.from({ length: 6 }, (_, index) => (
              <div key={index} className="card card-skeleton" />
            ))}
          </div>
        ) : null}

        {visible.length > 0 ? (
          <div className="grid">
            {visible.map((hackathon) => (
              <HackathonCard key={hackathon.id} hackathon={hackathon} />
            ))}
          </div>
        ) : null}

        {!loading && visible.length === 0 ? (
          <div className="empty">
            <h2>Nothing matches those filters</h2>
            <p>
              {sourceStates.every((s) => s.status === 'error')
                ? 'Every source is currently unavailable. Try refreshing in a moment.'
                : 'Try widening the travel radius, clearing the date range, or removing a theme.'}
            </p>
            <button
              type="button"
              className="button button-primary"
              onClick={() => setFilters(DEFAULT_FILTERS)}
            >
              Reset filters
            </button>
          </div>
        ) : null}
      </main>

      <footer className="site-footer">
        <p>
          Listings link back to their original source. HackFind never mirrors
          registration — always sign up on the organizer&apos;s own page.
        </p>
        <p className="attribution">
          Geocoding by Open-Meteo (GeoNames, CC BY 4.0). Sources cached for one hour.
        </p>
      </footer>
    </div>
  );
}
